import {
  Activity,
  Volume2,
  Sun,
  Music2,
  ShieldCheck,
  SlidersHorizontal,
  AlertTriangle,
  AlertCircle,
  Info,
  Check,
  PersonStanding,
} from 'lucide-react';
import { AnalysisResult } from '../lib/api';
import { analyzeAudioQuality, getMixingRecommendations } from '../utils/audioAnalysis';

interface Props {
  analysis: AnalysisResult;
  /** Compact hides Audio Insights (quality + mixing tips); Expanded shows them. */
  viewMode: 'compact' | 'expanded';
}

/**
 * The dense detail cards that used to live inside the top header — danceability /
 * loudness / dynamics / brightness, genre, and Audio Insights (expanded only).
 *
 * Pulled into its own component so ResultsPage can render it BELOW the player
 * instead of above it, while keeping ResultsPage focused on flow.
 */
export function TrackDetailCards({ analysis, viewMode }: Props) {
  const showAdvanced = (
    (analysis.danceability !== undefined && analysis.danceability !== null && analysis.danceability > 0) ||
    analysis.loudness ||
    analysis.dynamicRange ||
    analysis.spectral
  );
  const showGenre = analysis.genre && analysis.genre.confidence > 0;
  const showInsights = viewMode === 'expanded' && analysis.experimental?.audioflux_feature_set_v1;

  if (!showAdvanced && !showGenre && !showInsights) return null;

  return (
    <div className="track-detail-stack">
      {showAdvanced && (
        <div className="advanced-features-grid">
          {analysis.danceability !== undefined && analysis.danceability !== null && analysis.danceability > 0 && (
            <div className="feature-card danceability-card">
              <div className="feature-header">
                <div className="feature-icon"><PersonStanding size={16} strokeWidth={1.8} /></div>
                <div className="feature-label">Danceability</div>
              </div>
              <div className="feature-value">
                <div className="feature-number">{Math.round(analysis.danceability * 100)}</div>
                <div className="feature-unit">%</div>
              </div>
              <div className="feature-bar">
                <div
                  className="feature-bar-fill"
                  style={{ width: `${Math.max(analysis.danceability * 100, 1)}%` }}
                />
              </div>
            </div>
          )}

          {analysis.loudness && (
            <div className="feature-card loudness-card">
              <div className="feature-header">
                <div className="feature-icon"><Volume2 size={16} strokeWidth={1.8} /></div>
                <div className="feature-label">Loudness</div>
              </div>
              <div className="feature-value">
                <div className="feature-number">{analysis.loudness.integratedLufs?.toFixed(1)}</div>
                <div className="feature-unit">LUFS</div>
              </div>
              <div className="feature-hint">
                {analysis.loudness.integratedLufs && analysis.loudness.integratedLufs > -23
                  ? 'Above broadcast standard (-23 LUFS)'
                  : 'Broadcast compliant'}
              </div>
            </div>
          )}

          {analysis.dynamicRange && (
            <div className="feature-card dynamic-card">
              <div className="feature-header">
                <div className="feature-icon"><Activity size={16} strokeWidth={1.8} /></div>
                <div className="feature-label">Dynamic Range</div>
              </div>
              <div className="feature-value">
                <div className="feature-number">{analysis.dynamicRange.complexity?.toFixed(1)}</div>
                <div className="feature-unit">dB</div>
              </div>
              <div className="feature-hint">
                {analysis.dynamicRange.complexity && analysis.dynamicRange.complexity > 10
                  ? 'High dynamics'
                  : analysis.dynamicRange.complexity && analysis.dynamicRange.complexity > 6
                  ? 'Moderate dynamics'
                  : 'Compressed'}
              </div>
            </div>
          )}

          {analysis.spectral && analysis.spectral.centroid && (
            <div className="feature-card spectral-card">
              <div className="feature-header">
                <div className="feature-icon"><Sun size={16} strokeWidth={1.8} /></div>
                <div className="feature-label">Brightness</div>
              </div>
              <div className="feature-value">
                <div className="feature-number">{(analysis.spectral.centroid / 1000).toFixed(1)}</div>
                <div className="feature-unit">kHz</div>
              </div>
              <div className="feature-hint">
                {analysis.spectral.centroid > 3000
                  ? 'Bright'
                  : analysis.spectral.centroid > 2000
                  ? 'Moderate'
                  : 'Dark'}
              </div>
            </div>
          )}
        </div>
      )}

      {showGenre && analysis.genre && (
        <div className="audio-insights-section">
          <h2 className="section-title">
            <span className="section-icon"><Music2 size={20} strokeWidth={1.8} /></span>
            Genre
          </h2>
          <div className="insights-grid">
            <div className="insight-card genre-card">
              <div className="insight-header">
                <div className="insight-icon"><Music2 size={14} strokeWidth={2} /></div>
                <div className="insight-label">Predicted Genre</div>
              </div>
              <div className="insight-content">
                <div className="insight-main-value">{analysis.genre.primary}</div>
                <div className="insight-confidence">
                  {Math.round(analysis.genre.confidence * 100)}% confidence
                </div>
                <div className="insight-reasoning">{analysis.genre.reasoning}</div>
                {analysis.genre.alternatives && analysis.genre.alternatives.length > 0 && (
                  <details className="insight-details">
                    <summary>Other possibilities</summary>
                    <div className="genre-alternatives">
                      {analysis.genre.alternatives.slice(0, 3).map((pred, idx) => (
                        <div
                          key={idx}
                          className="genre-alternative"
                          style={{ ['--conf' as any]: `${Math.round(pred.confidence * 100)}%` }}
                        >
                          <span className="genre-name">{pred.genre}</span>
                          <span className="genre-conf">{Math.round(pred.confidence * 100)}%</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showInsights && analysis.experimental?.audioflux_feature_set_v1 && (
        <div className="audio-insights-section">
          <h2 className="section-title">
            <span className="section-icon"><SlidersHorizontal size={20} strokeWidth={1.8} /></span>
            Audio Insights
          </h2>

          <div className="insights-grid">
            {(() => {
              const quality = analyzeAudioQuality(analysis.experimental!.audioflux_feature_set_v1!);
              return (
                <div className="insight-card quality-card">
                  <div className="insight-header">
                    <div className="insight-icon"><ShieldCheck size={14} strokeWidth={2} /></div>
                    <div className="insight-label">Audio Quality</div>
                  </div>
                  <div className="insight-content">
                    <div className={`quality-badge quality-${quality.overallQuality}`}>
                      {quality.overallQuality.charAt(0).toUpperCase() + quality.overallQuality.slice(1)}
                    </div>
                    {quality.issues.length > 0 ? (
                      <div className="quality-issues">
                        {quality.issues.slice(0, 3).map((issue, idx) => (
                          <div key={idx} className={`quality-issue quality-${issue.type}`}>
                            <span className="issue-icon">
                              {issue.type === 'error' ? <AlertTriangle size={11} strokeWidth={2} /> : issue.type === 'warning' ? <AlertCircle size={11} strokeWidth={2} /> : <Info size={11} strokeWidth={2} />}
                            </span>
                            <span className="issue-text">{issue.message}</span>
                          </div>
                        ))}
                        {quality.issues.length > 3 && (
                          <details className="insight-details">
                            <summary>View all {quality.issues.length} issues</summary>
                            <div className="quality-issues">
                              {quality.issues.slice(3).map((issue, idx) => (
                                <div key={idx + 3} className={`quality-issue quality-${issue.type}`}>
                                  <span className="issue-icon">
                                    {issue.type === 'error' ? <AlertTriangle size={11} strokeWidth={2} /> : issue.type === 'warning' ? <AlertCircle size={11} strokeWidth={2} /> : <Info size={11} strokeWidth={2} />}
                                  </span>
                                  <span className="issue-text">{issue.message}</span>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    ) : (
                      <div className="quality-good"><Check size={12} strokeWidth={2.4} /> No issues detected — great quality.</div>
                    )}
                  </div>
                </div>
              );
            })()}

            {(() => {
              const recommendations = getMixingRecommendations(analysis.experimental!.audioflux_feature_set_v1!);
              return recommendations.length > 0 ? (
                <div className="insight-card mixing-card">
                  <div className="insight-header">
                    <div className="insight-icon"><SlidersHorizontal size={14} strokeWidth={2} /></div>
                    <div className="insight-label">Mixing Tips</div>
                  </div>
                  <div className="insight-content">
                    <div className="recommendations-list">
                      {recommendations.slice(0, 3).map((rec, idx) => (
                        <div key={idx} className={`recommendation rec-${rec.type} rec-${rec.severity}`}>
                          <div className="rec-header">
                            <span className="rec-type">{rec.type.toUpperCase()}</span>
                            <span className={`rec-severity severity-${rec.severity}`}>
                              {rec.severity}
                            </span>
                          </div>
                          <div className="rec-message">{rec.message}</div>
                        </div>
                      ))}
                      {recommendations.length > 3 && (
                        <details className="insight-details">
                          <summary>View all {recommendations.length} recommendations</summary>
                          <div className="recommendations-list">
                            {recommendations.slice(3).map((rec, idx) => (
                              <div key={idx + 3} className={`recommendation rec-${rec.type} rec-${rec.severity}`}>
                                <div className="rec-header">
                                  <span className="rec-type">{rec.type.toUpperCase()}</span>
                                  <span className={`rec-severity severity-${rec.severity}`}>
                                    {rec.severity}
                                  </span>
                                </div>
                                <div className="rec-message">{rec.message}</div>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
